import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Modal } from '@components-shared/modal/Modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { NEED_TO_UPDATE_MODAL_IS_SEEN_BY_USER } from 'components/shared/modal/constants';
import { PROFILE_STATUS } from 'global.enums';
import { IUser, LanguageType, IBrokerParams } from 'global.interfaces';
import type { i18n } from 'i18next';
import styles from './styles.module.scss';

interface IProps {
  user: IUser;
  brokerParams: IBrokerParams;
  currentLanguage: LanguageType;
}

type ModalContent = {
  mainContent: null | JSX.Element;
  footerContent: Array<JSX.Element>;
};

export const UserWithStatusNeedToUpdateModal = (props: IProps) => {
  const { i18n } = useTranslation();
  const { user } = props;
  const userStatus = user?.profile?.status;

  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    const needToShowModal = checkIfNeedToShowModal(userStatus);

    setShowModal(needToShowModal);
  }, [userStatus]);

  if (!showModal) return null;

  const { brokerParams, currentLanguage } = props;

  const closeModal = () => {
    localStorage.setItem(NEED_TO_UPDATE_MODAL_IS_SEEN_BY_USER, 'true');

    setShowModal(false);
  };

  const modalContent = getModalContent(user, brokerParams, currentLanguage, closeModal, i18n);
  const { mainContent, footerContent } = modalContent;

  return (
    <Modal
      title={i18n.t(keyMap.kycVerificationUpdateRequiredTitle)}
      showModalCloseButton={false}
      closeOnEscKey={false}
      classNameContainer={styles.needToUpdateContainer}
      modalFooter={footerContent}>
      {mainContent}
    </Modal>
  );
};

const checkIfNeedToShowModal = (userStatus: string) => {
  if (userStatus !== PROFILE_STATUS.UPDATEREQUIRED && userStatus !== PROFILE_STATUS.URGENTUPDATE) {
    return false;
  }

  return !localStorage.getItem(NEED_TO_UPDATE_MODAL_IS_SEEN_BY_USER);
};

const getModalContent = (
  user: IUser,
  brokerParams: IBrokerParams,
  currentLanguage: LanguageType,
  closeModal: () => void,
  i18n: i18n,
): ModalContent => {
  const { profile } = user;
  const { status } = profile;

  const navLinkButton = (
    <NavLink key="goToProfile" className="btn" onClick={closeModal} to="/member/profile">
      {i18n.t(keyMap.sharedActionsGoToProfile)}
    </NavLink>
  );

  if (status === PROFILE_STATUS.UPDATEREQUIRED) {
    const footerButton = (
      <button key="skip" type="button" className="btn light-white--button" onClick={closeModal}>
        {i18n.t(keyMap.sharedActionsLater)}
      </button>
    );

    return {
      mainContent: modalMainContent(
        i18n.t(keyMap.kycVerificationUpdateRequiredMsg, {
          daysUntilBlocked: user?.daysRemainingUntilUrgentUpdateRequired,
        }),
        i18n.t(keyMap.kycVerificationUpdateRequiredSecondLineMsg, {
          clientSupportEmail: brokerParams[currentLanguage]?.clientSupportEmail,
        }),
      ),
      footerContent: [footerButton, navLinkButton],
    };
  }

  const brokerParamsInCurrentLanguage = brokerParams[currentLanguage];

  return {
    mainContent: modalMainContent(
      i18n.t(keyMap.kycVerificationUrgentUpdateMsg),
      i18n.t(keyMap.kycVerificationUrgentUpdateSecondLineMsg, {
        brokerName: brokerParamsInCurrentLanguage?.brokerName,
        clientSupportEmail: brokerParamsInCurrentLanguage?.clientSupportEmail,
      }),
      styles.wordWhiteSpace,
    ),
    footerContent: [navLinkButton],
  };
};

const modalMainContent = (firstLineMessage: string, secondLineMessage: string, className?: string) => (
  <React.Fragment>
    <p className={className}>{firstLineMessage}</p>
    <p className={className}>{secondLineMessage}</p>
  </React.Fragment>
);
